import { useState, useContext, useEffect, useRef } from "react";
import { Dropdown } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { Trans } from "react-i18next";
import { AuthContext } from "../Context/AuthContext";
import { Authorization } from "../Context/Authorization";

/* ── Compact JFMC Type Switcher ─────────────────────────────── */
const JfmcTypeSwitcher = () => {
  const { selectedJfmcType, setSelectedJfmcType, role } = useContext(AuthContext);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    const fetchTypes = async () => {
      setLoading(true);
      try {
        const config = Authorization();
        const res = await fetch(`${Api}/api/jfmc-Types`, { headers: config.headers });
        const data = await res.json();
        setTypes(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchTypes();
  }, [Api]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const notSelected = !selectedJfmcType;

  return (
    <div ref={ref} style={{ position: "relative", display: "flex", alignItems: "center", gap: 8 }}>

      {/* ── Inline label ── */}
      <span style={{
        fontSize: 11, fontWeight: 600, color: "#718096",
        whiteSpace: "nowrap", letterSpacing: 0.2,
      }}>
        JFMC Type:
      </span>

      {/* ── Pill button — matches profile pill style ── */}
      <button
        onClick={() => setOpen(!open)}
        title={notSelected ? "Select a JFMC type to filter data across the platform" : `Active: ${selectedJfmcType.jfmcTypeName} — click to switch`}
        style={{
          display: "flex", alignItems: "center", gap: 7,
          padding: "5px 11px 5px 8px",
          border: notSelected ? "1px solid #F6AD55" : "1px solid #A5D6A7",
          borderRadius: 40,
          background: notSelected ? "#FFFAF0" : "#E8F5E9",
          color: notSelected ? "#C05621" : "#1B5E20",
          fontSize: 12, fontWeight: 600,
          cursor: "pointer", whiteSpace: "nowrap",
          transition: "all 0.18s",
          position: "relative",
        }}
      >
        {/* Status dot */}
        <span style={{
          width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
          background: notSelected ? "#ED8936" : "#2E7D32",
          boxShadow: notSelected
            ? "0 0 0 2px #FFFAF0, 0 0 0 4px rgba(237,137,54,0.35)"
            : "0 0 0 2px #E8F5E9, 0 0 0 4px rgba(46,125,50,0.2)",
          animation: notSelected ? "jfmcDot 1.6s ease-in-out infinite" : "none",
        }} />

        {notSelected ? "Select Type" : selectedJfmcType.jfmcTypeName}

        <i className={`mdi mdi-chevron-${open ? "up" : "down"}`}
          style={{ fontSize: 13, color: notSelected ? "#C05621" : "#2E7D32", marginLeft: 1 }} />
      </button>

      {/* ── Dropdown ── matches navbar .dropdown-menu style ── */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 10px)", right: 0, zIndex: 9999,
          background: "#fff", borderRadius: 12,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          border: "1px solid #E2E8F0", minWidth: 210, overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{
            padding: "10px 14px 8px",
            borderBottom: "1px solid #EDF2F7",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <i className="mdi mdi-tag-multiple-outline" style={{ fontSize: 15, color: "#2E7D32" }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#2D3748", lineHeight: 1.2 }}>Switch JFMC Type</div>
              <div style={{ fontSize: 10, color: "#A0AEC0", marginTop: 2 }}>Select your working category</div>
            </div>
          </div>

          {/* Options */}
          <div style={{ padding: "6px 8px" }}>
            {loading ? (
              <div style={{ padding: "12px 8px", fontSize: 12, color: "#A0AEC0", display: "flex", alignItems: "center", gap: 8 }}>
                <i className="mdi mdi-loading mdi-spin" style={{ fontSize: 14 }} /> Loading…
              </div>
            ) : types.map((t) => {
              const active = selectedJfmcType?._id === t._id;
              return (
                <button
                  key={t._id}
                  onClick={() => { setSelectedJfmcType(t); setOpen(false); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%",
                    padding: "9px 10px", border: "none", borderRadius: 8,
                    cursor: "pointer", textAlign: "left",
                    background: active ? "#E8F5E9" : "transparent",
                    color: active ? "#1B5E20" : "#2D3748",
                    fontSize: 13, fontWeight: active ? 700 : 500,
                    transition: "background 0.12s",
                    marginBottom: 2,
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "#F7FAFC"; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
                >
                  <span style={{
                    width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                    background: active ? "#2E7D32" : "#CBD5E0",
                  }} />
                  <span style={{ flex: 1 }}>{t.jfmcTypeName}</span>
                  {active && <i className="mdi mdi-check-circle" style={{ color: "#2E7D32", fontSize: 15 }} />}
                </button>
              );
            })}
          </div>

          {/* Clear */}
          {selectedJfmcType && (
            <>
              <div style={{ height: 1, background: "#EDF2F7", margin: "0 8px" }} />
              <div style={{ padding: "6px 8px" }}>
                <button
                  onClick={() => { setSelectedJfmcType(null); setOpen(false); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, width: "100%",
                    padding: "8px 10px", border: "none", borderRadius: 8,
                    cursor: "pointer", background: "transparent",
                    color: "#718096", fontSize: 12, fontWeight: 500,
                    transition: "background 0.12s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#FFF5F5"; e.currentTarget.style.color = "#E53E3E"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#718096"; }}
                >
                  <i className="mdi mdi-close-circle-outline" style={{ fontSize: 14 }} />
                  Clear Selection
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <style>{`
        @keyframes jfmcDot {
          0%, 100% { box-shadow: 0 0 0 2px #FFFAF0, 0 0 0 4px rgba(237,137,54,0.35); }
          50%       { box-shadow: 0 0 0 2px #FFFAF0, 0 0 0 7px rgba(237,137,54,0.08); }
        }
      `}</style>
    </div>
  );
};

/* ── Main Navbar ──────────────────────────────────────────────── */
const Navbar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { clearAuth, name, role } = useContext(AuthContext);
  const navigate = useNavigate();

  const toggleOffcanvas = () => {
    setSidebarOpen(!sidebarOpen);
    document.querySelector(".sidebar-offcanvas").classList.toggle("active");
  };

  const handleLogout = () => {
    clearAuth();
    localStorage.clear();
    sessionStorage.clear();
    navigate("/");
  };
  const JFMC_TYPE_EXCLUDED_ROLES = [
    "Super Admin",
  ];
  return (
    <nav className="navbar default-layout-navbar col-lg-12 col-12 p-0 fixed-top d-flex flex-row">
      <div className="text-center navbar-brand-wrapper d-flex align-items-center justify-content-center">
        <Link className="navbar-brand brand-logo" to="/dashboard">JFMC</Link>
        <Link className="navbar-brand brand-logo-mini" to="/dashboard">
          <img src={require("../../assets/images/auth/images.jpg")} alt="logo" />
        </Link>
      </div>

      <div className="navbar-menu-wrapper d-flex align-items-stretch">
        <button
          className="navbar-toggler navbar-toggler align-self-center"
          type="button"
          onClick={() => document.querySelector(".container-scroller").classList.toggle("sidebar-icon-only")}
        >
          <span className="mdi mdi-menu" />
        </button>

        <ul className="navbar-nav navbar-nav-right">
          {/* JFMC Type Switcher */}
          {!JFMC_TYPE_EXCLUDED_ROLES.includes(role) && (
            <li className="nav-item d-flex align-items-center" style={{ marginRight: 8 }}>
              <JfmcTypeSwitcher />
            </li>)}

          <li className="nav-item nav-profile">
            <Dropdown alignRight>
              <Dropdown.Toggle className="nav-link">
                <div className="nav-profile-img">
                  <img src={require("../../assets/images/faces/default-face.png")} alt="user" />
                  <span className="availability-status online" />
                </div>
                <div className="nav-profile-text">
                  <p className="mb-1 text-black"><Trans>{name}</Trans></p>
                </div>
              </Dropdown.Toggle>
              <Dropdown.Menu className="navbar-dropdown">
                {["Admin", "Super Admin"].includes(role) && (
                  <Dropdown.Item onClick={() => navigate("/activity-log")}>
                    <i className="mdi mdi-cached mr-3 text-success" />
                    <Trans>Activity Log</Trans>
                  </Dropdown.Item>
                )}

                {["Admin", "Super Admin"].includes(role) && (
                  <Dropdown.Item onClick={() => navigate("/weekly-activity")}>
                    <i className="fas fa-tasks mr-3 text-success" />
                    <Trans>Weekly Log</Trans>
                  </Dropdown.Item>
                )}
                <Dropdown.Item href="!#" onClick={handleLogout}>
                  <i className="mdi mdi-logout mr-3 text-primary" />
                  <Trans>Signout</Trans>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </li>
          <li className="nav-item nav-logout d-none d-lg-block">
            <a className="nav-link" href="!#" onClick={handleLogout}>
              <i className="mdi mdi-power" />
            </a>
          </li>
        </ul>

        <button
          className="navbar-toggler navbar-toggler-right d-lg-none align-self-center"
          type="button"
          onClick={toggleOffcanvas}
        >
          <span className="mdi mdi-menu" />
        </button>
      </div>
    </nav>
  );
};

export default Navbar;
